import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useInventoryItem,
  useUpdateInventoryItem,
  useCreateMovement,
  useInventoryCategories,
  useInventoryLocations,
  useCreateCategory,
  useCreateLocation,
} from '../../../platform/api/hooks';
import { asRecord, str, asList } from '../../../platform/api/utils';
import { ApiError } from '../../../platform/api/client';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

import {
  type EditForm,
  type MovementForm,
  type Tab,
  STATUS_LABELS,
  STATUS_VARIANTS,
  editFormSchema,
  movementFormSchema,
  SkeletonDetail,
  linkBtn,
  secondaryBtn,
} from './inventory-detail.support';
import {
  InventoryDadosTab,
  InventoryMovementsTab,
  InventoryMovementModal,
} from './inventory-detail.panels';
import { usePermissions } from '../../../platform/auth/permissions';

export function InventoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: item, isLoading, isError } = useInventoryItem(id ?? '');
  const { can } = usePermissions();
  const updateItem = useUpdateInventoryItem();
  const createMovement = useCreateMovement();
  const createCategory = useCreateCategory();
  const createLocation = useCreateLocation();
  const { data: catData } = useInventoryCategories();
  const { data: locData } = useInventoryLocations();
  const categories = asList(catData);
  const locations = asList(locData);

  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('dados');
  const [movementOpen, setMovementOpen] = useState(false);
  const [movementError, setMovementError] = useState('');

  const it = asRecord(item);
  const st = str(it.status);
  const vis = str(it.visibility);
  const cat = it.category as Record<string, unknown> | undefined;
  const loc = it.location as Record<string, unknown> | undefined;
  const movements = asList(it.movements);

  const form = useForm<EditForm>({
    resolver: zodResolver(editFormSchema),
    values: {
      name: str(it.name),
      unit: str(it.unit),
      minimumQuantity: it.minimumQuantity ? str(it.minimumQuantity) : '',
      categoryId: str(it.categoryId),
      locationId: str(it.locationId),
      description: str(it.description),
      visibility: vis,
      isPerishable: !!it.isPerishable,
      requiresHouseValidation: !!it.requiresHouseValidation,
      controlledOrSensitive: !!it.controlledOrSensitive,
      expiryDate: typeof it.expiryDate === 'string' ? it.expiryDate.substring(0, 10) : '',
    },
  });

  const mvForm = useForm<MovementForm>({
    resolver: zodResolver(movementFormSchema),
    defaultValues: { type: 'OUTGOING', quantity: '', reason: '' },
  });

  if (isLoading) return <SkeletonDetail />;
  if (isError || !id) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', margin: 0 }}>Não foi possível carregar este item.</p>
          <button
            type="button"
            onClick={() => {
              void navigate('/estoque');
            }}
            style={linkBtn}
          >
            ← Voltar para estoque
          </button>
        </Section>
      </div>
    );
  }

  function handleSave(data: EditForm) {
    if (!id) return;
    updateItem.mutate({
      id,
      data: {
        name: data.name,
        unit: data.unit,
        minimumQuantity: data.minimumQuantity !== '' ? data.minimumQuantity : undefined,
        categoryId: data.categoryId !== '' ? data.categoryId : undefined,
        locationId: data.locationId !== '' ? data.locationId : undefined,
        description: data.description !== '' ? data.description : undefined,
        visibility: data.visibility,
        isPerishable: data.isPerishable,
        requiresHouseValidation: data.requiresHouseValidation,
        controlledOrSensitive: data.controlledOrSensitive,
        expiryDate: data.expiryDate !== '' ? data.expiryDate : undefined,
      },
    });
    setEditing(false);
  }

  async function handleMovement(data: MovementForm) {
    if (!id) return;
    setMovementError('');
    const qty = Number(data.quantity);
    const balance = Number(it.currentQuantity);
    if (
      (data.type === 'OUTGOING' || data.type === 'LOSS_DISPOSAL') &&
      Number.isFinite(qty) &&
      Number.isFinite(balance) &&
      qty > balance
    ) {
      setMovementError(
        `Quantidade insuficiente em estoque. Saldo atual: ${str(it.currentQuantity)} ${str(it.unit)}.`,
      );
      return;
    }
    try {
      await createMovement.mutateAsync({
        id,
        data: {
          type: data.type,
          quantity: data.quantity,
          reason: data.reason !== '' ? data.reason : undefined,
        },
      });
      setMovementOpen(false);
      setMovementError('');
      mvForm.reset();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : 'Não foi possível registrar a movimentação.';
      setMovementError(msg);
    }
  }

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <button
        type="button"
        onClick={() => {
          void navigate('/estoque');
        }}
        style={linkBtn}
      >
        ← Voltar para estoque
      </button>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.25rem',
          marginTop: '0.5rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{str(it.name)}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
            <Badge label={STATUS_LABELS[st] ?? st} variant={STATUS_VARIANTS[st] ?? 'default'} />
            {cat && (
              <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{str(cat.name)}</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {!editing && can('inventory.items.update') && (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
              }}
              style={secondaryBtn}
            >
              Editar
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setMovementOpen(true);
              setMovementError('');
              mvForm.reset();
            }}
            style={{
              ...secondaryBtn,
              background: 'var(--info-soft)',
              borderColor: 'var(--info-soft)',
              color: 'var(--info)',
            }}
          >
            + Movimentação
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: '1rem',
          borderBottom: '2px solid var(--line)',
        }}
      >
        {(
          [
            ['dados', 'Dados'],
            ['movimentacoes', `Movimentações (${String(movements.length)})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setActiveTab(key);
            }}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              fontWeight: activeTab === key ? 600 : 400,
              color: activeTab === key ? 'var(--green)' : 'var(--muted)',
              border: 'none',
              borderBottom: activeTab === key ? '2px solid var(--green)' : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {activeTab === 'dados' && (
          <InventoryDadosTab
            editing={editing}
            form={form}
            it={it}
            st={st}
            vis={vis}
            cat={cat}
            loc={loc}
            categories={categories}
            locations={locations}
            createCategory={createCategory}
            createLocation={createLocation}
            updateItem={updateItem}
            onCancelEdit={() => {
              setEditing(false);
            }}
            onSave={(data) => {
              handleSave(data);
            }}
          />
        )}
        {activeTab === 'movimentacoes' && <InventoryMovementsTab movements={movements} />}
        {movementOpen && (
          <InventoryMovementModal
            it={it}
            mvForm={mvForm}
            movementError={movementError}
            createMovement={createMovement}
            onClose={() => {
              setMovementOpen(false);
              setMovementError('');
              mvForm.reset();
            }}
            onSubmit={() => {
              void mvForm.handleSubmit(handleMovement)();
            }}
          />
        )}
      </div>
    </div>
  );
}
